/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { InputAction } from "../enums/InputAction";
import { Log } from "../logging/Log";
import type { TInputEvent, InputEventType } from "./types/TInputEvent";
import type { ModalInputHandler } from "./types/TModalController";
import type { IModalController } from "./interfaces/IModalController";

/**
 * Base class for modal input controllers.
 *
 * Each modal registers handlers for specific InputAction + eventType combinations.
 * The active ScreenController routes input to the top modal in its stack first;
 * unhandled events bubble down to the next modal or to the screen itself.
 *
 * Subclasses MUST implement `initialize()` to register their handlers.
 */
export abstract class ModalController implements IModalController {
  private static TAG: string = "ModalController";

  public readonly name: string;

  private mHandlers: Map<string, ModalInputHandler> = new Map();

  constructor(name: string) {
    this.name = name;
    this.initialize();
  }

  protected abstract initialize(): void;

  protected registerHandler(
    action: InputAction,
    handler: ModalInputHandler,
    eventType?: InputEventType | "all"
  ): void {
    const resolvedType =
      eventType !== null && eventType !== undefined
        ? eventType
        : this.defaultEventType(action);

    if (resolvedType === "all") {
      this.mHandlers.set(`${action}:*`, handler);
    } else {
      this.mHandlers.set(`${action}:${resolvedType}`, handler);
    }
  }

  private defaultEventType(action: InputAction): InputEventType {
    switch (action) {
      case InputAction.POINTER_CLICK:
        return "click";
      case InputAction.POINTER_CONTEXT_MENU:
        return "contextmenu";
      case InputAction.POINTER_HOVER:
        return "mouseenter";
      case InputAction.POINTER_LEAVE:
        return "mouseleave";
      case InputAction.SCROLL_UP:
      case InputAction.SCROLL_DOWN:
        return "wheel";
      default:
        return "keydown";
    }
  }

  public handleInput(event: TInputEvent): boolean {
    const exactKey = `${event.action}:${event.eventType}`;
    const wildcardKey = `${event.action}:*`;
    const exactHandler = this.mHandlers.get(exactKey);
    const handler =
      exactHandler === undefined
        ? this.mHandlers.get(wildcardKey)
        : exactHandler;

    if (handler) {
      try {
        return handler(event);
      } catch (error: unknown) {
        Log.e(
          ModalController.TAG,
          `handleInput() — handler error in modal "${this.name}" for ${exactKey}`,
          error
        );
        return false;
      }
    }

    return false;
  }

  public cleanup(): void {
    this.mHandlers.clear();
  }
}
